
import { DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import {
  LucideCircleDot,
  LucideDroplets,
  LucideShovel,
  LucideSprout,
  LucideTractor,
  LucideWheat,
} from '@lucide/angular';

import { CropIcon } from '../../shared/ui/crop-icon/crop-icon';
import { LanguageService } from '../../core/i18n/language.service';
import { TranslationKey } from '../../core/i18n/translations/en';
import { ActiveFarmService } from '../../core/services/active-farm.service';
import { ActiveGameServerService } from '../../core/services/active-game-server.service';
import { FieldService } from '../../core/services/field.service';
import { GameStateService } from '../../core/services/game-state.service';
import {
  CreateTaskRequest,
  TaskService,
} from '../../core/services/task.service';
import { Field } from '../../models/field.model';
import { GameState } from '../../models/game-state.model';
import { Task } from '../../models/task.model';
import { Dialog } from '../../shared/ui/dialog/dialog';
import { TaskForm } from '../tasks/task-form/task-form';

import { CalendarFruitType } from '../calendar/calendar.model';
import { CalendarService } from '../calendar/calendar.service';
import {
  FieldCalendarInfo,
  getFieldCalendarInfo,
} from '../fields/field-calendar';

interface DisplayedGameState {
  period: number;
  day: number;
  currentHour: number;
}

@Component({
  imports: [
    CropIcon,
    RouterLink,
    DecimalPipe,
    Dialog,
    TaskForm,
    LucideSprout,
    LucideDroplets,
    LucideWheat,
    LucideCircleDot,
    LucideTractor,
    LucideShovel,
  ],
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard {
  private readonly activeFarmService =
    inject(ActiveFarmService);

  private readonly activeGameServerService =
    inject(ActiveGameServerService);

  private readonly calendarService = inject(CalendarService);

  private readonly fieldService = inject(FieldService);
  private readonly gameStateService = inject(GameStateService);
  private readonly taskService = inject(TaskService);
  private readonly destroyRef = inject(DestroyRef);

  readonly i18n = inject(LanguageService);

  readonly fields = signal<Field[]>([]);
  readonly tasks = signal<Task[]>([]);
  readonly gameState = signal<GameState | null>(null);
  readonly fruitTypes = signal<CalendarFruitType[]>([]);

  readonly displayedGameState =
    signal<DisplayedGameState | null>(null);

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly planningOpen = signal(false);
  readonly taskError = signal('');

  private gameClockInterval:
    ReturnType<typeof setInterval> | null = null;

  @ViewChild(TaskForm)
  private taskForm?: TaskForm;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.stopGameClock();
    });

    effect(() => {
      const gameServerId =
        this.activeGameServerService.activeGameServerId();

      if (gameServerId === null) {
        this.gameState.set(null);
        this.displayedGameState.set(null);
        this.stopGameClock();
        return;
      }

      this.loadGameState();
    });

    effect(() => {
      const farmId =
        this.activeFarmService.activeFarmId();

      if (farmId === null) {
        this.fields.set([]);
        this.tasks.set([]);

        this.loading.set(
          this.activeFarmService.loading(),
        );

        return;
      }

      this.loadFields(farmId);
      this.loadTasks(farmId);
    });
  }

  get totalFields(): number {
    return this.fields().length;
  }

  get totalArea(): number {
    return this.fields().reduce(
      (sum, field) => sum + field.area,
      0,
    );
  }

  get nextHarvestField(): Field | undefined {
    return this.fields().find(
      (field) => field.growthStatus === 'HARVESTABLE',
    );
  }

  get openTasks(): number {
    return this.tasks().filter(
      (task) => task.status !== 'done',
    ).length;
  }

  get nextTasks(): Task[] {
    return this.tasks()
      .filter((task) => task.status !== 'done')
      .slice(0, 3);
  }

  isHarvestReady(field: Field): boolean {
    return field.growthStatus === 'HARVESTABLE';
  }

  sowingWindowLabel(field: Field): string | null {
    const period = this.displayedGameState()?.period;
    const cropCode = field.plannedCrop?.code;

    if (!period || !cropCode) {
      return null;
    }

    // Spielperioden: 1 = März, 2 = April, ... 12 = Februar.
    // Vorläufige Standardkalenderwerte.
    // Für Mod-Früchte und abweichende Kartenkalender
    // müssen die Zeiträume später ergänzt werden.
    const sowingPeriods: Record<string, readonly number[]> = {
      wheat: [7, 8],
      barley: [7, 8],
      oat: [1, 2],
      canola: [6, 7],
      sorghum: [2, 3],
      maize: [2, 3],
      corn: [2, 3],
      sunflower: [1, 2],
      soybean: [2, 3],
      potato: [1, 2],
      sugarbeet: [1, 2],
      cotton: [2, 3],
    };

    const months = sowingPeriods[cropCode.toLowerCase()];

    if (!months) {
      return null;
    }

    return months.includes(period)
      ? 'Aussaatzeit'
      : null;
  }

  cropLabel(crop: string): string {
    return this.i18n.cropLabel(crop);
  }

  taskLabel(type: string): string {
    const taskKeys: Record<string, TranslationKey> = {
      harvest: 'task.harvest',
      fertilize: 'task.fertilize',
      sow: 'task.sow',
      roll: 'task.roll',
      cultivate: 'task.cultivate',
      plow: 'task.plow',
    };

    const key = taskKeys[type];

    return key
      ? this.i18n.t(key)
      : this.i18n.t('task.unknown');
  }

  taskStatusLabel(status: Task['status']): string {
    const statusKeys: Record<Task['status'], TranslationKey> = {
      open: 'task.status.open',
      planned: 'task.status.planned',
      done: 'task.status.done',
    };

    return this.i18n.t(statusKeys[status]);
  }

  gameMonthLabel(period: number): string {
    const monthKeys: TranslationKey[] = [
      'gameState.month.march',
      'gameState.month.april',
      'gameState.month.may',
      'gameState.month.june',
      'gameState.month.july',
      'gameState.month.august',
      'gameState.month.september',
      'gameState.month.october',
      'gameState.month.november',
      'gameState.month.december',
      'gameState.month.january',
      'gameState.month.february',
    ];

    const key = monthKeys[period - 1];

    return key
      ? this.i18n.t(key)
      : `${this.i18n.t('gameState.month.unknown')} ${period}`;
  }

  gameTimeLabel(currentHour: number): string {
    const totalMinutes = Math.floor(currentHour * 60);

    const normalizedMinutes =
      ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);

    const hours = Math.floor(normalizedMinutes / 60);
    const minutes = normalizedMinutes % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  }

  openPlanning(): void {
    this.taskError.set('');
    this.planningOpen.set(true);
  }

  closePlanning(): void {
    this.taskError.set('');
    this.planningOpen.set(false);
  }

  createTask(task: CreateTaskRequest): void {
    this.taskError.set('');

    this.taskService.createTask(task).subscribe({
      next: () => {
        this.taskForm?.reset();
        this.closePlanning();

        const farmId =
          this.activeFarmService.activeFarmId();

        if (farmId !== null) {
          this.loadTasks(farmId);
        }
      },
      error: (error: unknown) => {
        const response = error as {
          error?: { message?: unknown };
        };

        const message = response?.error?.message;

        this.taskError.set(
          typeof message === 'string'
            ? message
            : 'Die Aufgabe konnte nicht gespeichert werden.',
        );

        console.error('Could not create task:', error);
      },
    });
  }

  private loadGameState(): void {
    this.gameStateService.getGameState().subscribe({
      next: (gameState) => {
        this.gameState.set(gameState);
        this.startGameClock(gameState);
      },
      error: () => {
        this.gameState.set(null);
        this.displayedGameState.set(null);
        this.stopGameClock();
      },
    });
  }

  private startGameClock(gameState: GameState): void {
    this.stopGameClock();

    const updateClock = () => {
      this.displayedGameState.set(
        this.calculateDisplayedGameState(gameState),
      );
    };

    updateClock();

    if (gameState.paused) {
      return;
    }

    this.gameClockInterval = setInterval(
      updateClock,
      1000,
    );
  }

  private calculateDisplayedGameState(
    gameState: GameState,
  ): DisplayedGameState {
    const syncedAt =
      new Date(gameState.syncedAt).getTime();

    const elapsedRealSeconds =
      gameState.paused || !Number.isFinite(syncedAt)
        ? 0
        : Math.max(
          0,
          (Date.now() - syncedAt) / 1000,
        );

    const elapsedGameHours =
      (elapsedRealSeconds * gameState.timeScale) / 3600;

    const totalGameHours =
      gameState.currentHour + elapsedGameHours;

    const advancedDays =
      Math.floor(totalGameHours / 24);

    const currentHour =
      ((totalGameHours % 24) + 24) % 24;

    const totalDayIndex =
      gameState.day - 1 + advancedDays;

    const advancedPeriods =
      Math.floor(
        totalDayIndex / gameState.daysPerMonth,
      );

    const day =
      ((totalDayIndex % gameState.daysPerMonth) +
        gameState.daysPerMonth) %
      gameState.daysPerMonth +
      1;

    const period =
      ((gameState.period - 1 + advancedPeriods) % 12 +
        12) %
      12 +
      1;

    return {
      period,
      day,
      currentHour,
    };
  }

  private stopGameClock(): void {
    if (this.gameClockInterval !== null) {
      clearInterval(this.gameClockInterval);
      this.gameClockInterval = null;
    }
  }

  private loadFields(farmId: number): void {
    this.loading.set(true);
    this.error.set(false);

    this.fieldService.getFields(farmId).subscribe({
      next: (fields) => {
        this.fields.set(fields);
        this.loading.set(false);
      },
      error: () => {
        this.fields.set([]);
        this.error.set(true);
        this.loading.set(false);
      },
    });
  }

  private loadTasks(farmId: number): void {
    this.taskService.getTasks(farmId).subscribe({
      next: (tasks) => {
        this.tasks.set(tasks);
      },
      error: () => {
        this.tasks.set([]);
      },
    });
  }
  calendarInfo(field: Field): FieldCalendarInfo {
    return getFieldCalendarInfo(
      field,
      this.fruitTypes(),
      this.displayedGameState()?.period ?? null,
    );
  }

  private loadCalendar(): void {
    this.calendarService.getFruitTypes().subscribe({
      next: (fruitTypes) => {
        this.fruitTypes.set(fruitTypes);
      },
      error: () => {
        this.fruitTypes.set([]);
      },
    });
  }
}
